import { useState } from "react";

import { BASE_URL } from "../../../features/api/apiSlice";
import {
  useAdminFirmsQuery,
  useAdminFirmsSummaryQuery,
  useCreateFirmAccountMutation,
  useRecordFirmPaymentMutation,
  useUpdateFirmDetailsMutation,
  useSearchFirmPartnersQuery,
} from "../api/firmApi";
import { useGetLedgerAccountsQuery } from "../../billing/api/billingApi";
import {
  useGetStatesQuery,
  useGetChaptersQuery,
} from "../../settings/api/settingsApi";

import { useAccess } from "../../../utils/useAccess";
import { PERMS } from "../../../utils/perms";
import DataTable from "../../membership/components/DataTable";
import TableSkeleton from "../../membership/components/TableSkeleton";
import useDebounce from "../../membership/components/useDebounce";
import { formatCurrency, formatDate } from "../../../utils/formatters";

/**
 * "Main Operations — 1001 · Zenith Bank · 1012345678"
 *
 * The ledger account a payment is being posted into, named the way the person
 * holding the teller slip sees it. The account's own name alone is not enough
 * to pick with: the Institute runs several accounts with names that read alike,
 * and the CODE is what finance reconciles against — the slip carries the bank
 * and the number, so both are here to be matched by eye before the money is
 * posted to the wrong place.
 *
 * Each part is dropped when it is missing rather than stubbed, and the id is
 * the last resort: an option reading "#7" is at least selectable.
 */
function ledgerAccountLabel(a: any): string {
  const name = [a?.name, a?.code].filter(Boolean).join(" — ");
  const bank = [a?.bank_name, a?.account_number].filter(Boolean).join(" · ");

  return [name || a?.account_name, bank].filter(Boolean).join(" · ") || `#${a?.id}`;
}

/**
 * The Institute's register of PRACTICE FIRMS — the firm counterpart of the
 * member directory at /member-list, and built on the same table so the two are
 * read the same way.
 *
 * Deliberately NOT the firm applications screen. An application is paperwork
 * moving through a review and belongs in a queue; a firm is a standing entry
 * that outlives it. A firm appears here the moment its number is issued, and
 * looking one up years later is this page's whole job.
 *
 * Paged on the server, unlike the applications queue: the register only grows,
 * so a screen that pulled all of it would get slower every year.
 */
export default function FirmRegistryPage() {
  // What this person may actually DO here. Reading the register is one
  // privilege; exporting it, creating an account and taking money are three
  // more — so the controls for those are not rendered to somebody who would
  // only be refused by the API. See PERMS.firm / FirmPermissionSeeder.
  const { can } = useAccess();
  const canExport  = can.perm(PERMS.firm.export);
  const canAccount = can.perm(PERMS.firm.account);
  const canPay     = can.perm(PERMS.firm.payment);
  const canEdit    = can.perm(PERMS.firm.update);

  // Logic states — same shape as MembershipManagement.
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("approved_at");
  const [dir, setDir] = useState("desc");

  // Filter states
  const [status, setStatus] = useState("");
  const [stateId, setStateId] = useState("");
  const [chapterId, setChapterId] = useState("");
  // Date the firm was registered (its number issued), as a range.
  const [regFrom, setRegFrom] = useState("");
  const [regTo, setRegTo] = useState("");
  // Register entries with nobody on the roster — a firm no partner answers for.
  const [noPartners, setNoPartners] = useState("");
  // Firms with no account of their own — billed all the same, but with nobody
  // to send the bill to.
  const [noAccount, setNoAccount] = useState("");
  // Firms carrying arrears. The invoices belong to the FIRM, so this is a
  // question about the firm rather than about anybody's account.
  const [owing, setOwing] = useState("");

  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = [
    status, stateId, chapterId, regFrom, regTo, noPartners, noAccount, owing,
  ].filter(Boolean).length;

  const debouncedSearch = useDebounce(search);

  // The filters the summary counts by. `status` is left out on purpose: the
  // status cards are how the status filter is chosen, so they have to show what
  // choosing one would give.
  const commonFilters = {
    search: debouncedSearch,
    state_id: stateId,
    chapter_id: chapterId,
    approved_from: regFrom,
    approved_to: regTo,
    no_partners: noPartners === "1",
    no_account: noAccount === "1",
    owing: owing === "1",
  };

  const { data: firms, isLoading, isError, error: listError } = useAdminFirmsQuery({
    ...commonFilters,
    status,
    page,
    per_page: perPage,
    sort_by: sort,
    sort_dir: dir,
  });

  const { data: summary } = useAdminFirmsSummaryQuery(commonFilters);
  const { data: states } = useGetStatesQuery();
  const { data: chapters } = useGetChaptersQuery();

  const [selected, setSelected] = useState([]);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Attaching an account to a firm that has none. The firm being worked on, the
  // address being given it, and whatever the server said about it.
  const [accountFor, setAccountFor] = useState<any | null>(null);
  const [accountEmail, setAccountEmail] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountError, setAccountError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [createAccount, { isLoading: creatingAccount }] = useCreateFirmAccountMutation();

  const openAccountDialog = (firm: any) => {
    setAccountFor(firm);
    // Prefilled from the firm's own particulars — this is the firm's account,
    // so its own address is the right one where it has one.
    setAccountEmail(firm?.email ?? "");
    setAccountName(firm?.name ?? "");
    setAccountError(null);
  };

  /*
   * Correcting what the register says about a firm.
   *
   * Two fields, both of which were fixed at registration and unreachable
   * afterwards: the address the Institute writes to, and who the principal
   * partner is. A practice moves office; a principal retires. Neither should
   * need a hand-written UPDATE to put right.
   *
   * The principal is held as an id AND a label: the id is what the server
   * stores, and the label is what the person who picked it is looking at —
   * re-deriving the name from a search result that has since been replaced is
   * how a dialog ends up showing one member while saving another.
   */
  const [editFor, setEditFor] = useState<any | null>(null);
  const [editEmail, setEditEmail] = useState("");
  const [editPrincipalId, setEditPrincipalId] = useState<number | null>(null);
  const [editPrincipalLabel, setEditPrincipalLabel] = useState<string>("");
  const [principalSearch, setPrincipalSearch] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [updateFirm, { isLoading: savingEdit }] = useUpdateFirmDetailsMutation();

  // The member picker. Short terms are not searched: two characters match half
  // the register and the answer is useless to whoever typed them.
  const principalTerm = useDebounce(principalSearch, 400);
  const { data: principalResults = [], isFetching: searchingPrincipals } =
    useSearchFirmPartnersQuery(principalTerm, { skip: principalTerm.trim().length < 3 });

  const openEditDialog = (firm: any) => {
    setEditFor(firm);
    setEditEmail(firm?.email ?? "");
    setEditPrincipalId(firm?.principal_user_id ?? null);
    setEditPrincipalLabel(
      firm?.principal?.name
        ? `${firm.principal.name}${
            firm.principal.member?.membership_no ? ` · ${firm.principal.member.membership_no}` : ""
          }`
        : "",
    );
    setPrincipalSearch("");
    setEditError(null);
  };

  const submitEdit = async () => {
    if (!editFor) return;
    setEditError(null);

    // Only what actually changed. An absent key means "leave it alone" to the
    // server, so sending both every time would let a dialog opened to fix an
    // address quietly rewrite the principal to whatever it had loaded.
    const body: any = { id: editFor.id };
    const emailNow = editEmail.trim();
    if (emailNow !== String(editFor.email ?? "")) body.email = emailNow;
    if ((editPrincipalId ?? null) !== (editFor.principal_user_id ?? null)) {
      body.principal_user_id = editPrincipalId;
    }

    if (Object.keys(body).length === 1) {
      setEditFor(null);
      return;
    }

    try {
      const res: any = await updateFirm(body).unwrap();
      setEditFor(null);
      setBanner(res?.message ?? "The firm's particulars have been updated.");
      setTimeout(() => setBanner(null), 8000);
    } catch (e: any) {
      setEditError(
        e?.data?.message ||
          Object.values(e?.data?.errors ?? {})[0]?.toString() ||
          "Could not update the firm.",
      );
    }
  };

  // Recording money a firm paid at the bank. Everything the teller tells us,
  // plus the slip itself.
  const [payFor, setPayFor] = useState<any | null>(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("bank_transfer");
  const [payReference, setPayReference] = useState("");
  const [payPaidAt, setPayPaidAt] = useState("");
  const [payBankAccountId, setPayBankAccountId] = useState("");
  const [payNotes, setPayNotes] = useState("");
  const [payProof, setPayProof] = useState<File | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [recordPayment, { isLoading: recording }] = useRecordFirmPaymentMutation();
  const { data: bankAccounts = [] } = useGetLedgerAccountsQuery();

  const openPaymentDialog = (firm: any) => {
    setPayFor(firm);
    // Prefilled with what the firm owes — the common case is a firm clearing
    // its balance, and retyping a figure that is already on screen is how a
    // wrong one gets entered.
    setPayAmount(String(Number(firm?.outstanding_kobo ?? 0) / 100));
    setPayMethod("bank_transfer");
    setPayReference("");
    setPayPaidAt(new Date().toISOString().slice(0, 10));
    setPayBankAccountId("");
    setPayNotes("");
    setPayProof(null);
    setPayError(null);
  };

  const submitPayment = async () => {
    if (!payFor) return;
    setPayError(null);

    const body = new FormData();
    body.append("amount", payAmount);
    body.append("payment_method", payMethod);
    body.append("reference", payReference.trim());
    body.append("paid_at", payPaidAt);
    body.append("bank_account_id", payBankAccountId);
    if (payNotes.trim()) body.append("notes", payNotes.trim());
    if (payProof) body.append("proof_of_payment", payProof);

    try {
      const res: any = await recordPayment({ id: payFor.id, body }).unwrap();
      setPayFor(null);
      setBanner(res?.message ?? "Payment recorded.");
      setTimeout(() => setBanner(null), 8000);
    } catch (e: any) {
      setPayError(
        e?.data?.message ||
          Object.values(e?.data?.errors ?? {})[0]?.toString() ||
          "Could not record the payment.",
      );
    }
  };

  const submitAccount = async () => {
    if (!accountFor) return;
    setAccountError(null);
    try {
      const res: any = await createAccount({
        id: accountFor.id,
        email: accountEmail.trim(),
        name: accountName.trim() || undefined,
      }).unwrap();

      setAccountFor(null);
      // The server says whether an invoice was raised and whether the firm's
      // password reached it — repeating a guess here is how the screen ends up
      // promising a bill that was never created, or a password nobody got.
      // Held longer than the other banners for that reason: where the mail
      // failed, this line is the only place it is said.
      setBanner(res?.message ?? "Account created.");
      setTimeout(() => setBanner(null), 14000);
    } catch (e: any) {
      setAccountError(e?.data?.message || "Could not create the account.");
    }
  };

  /**
   * The whole filtered register as a CSV — every firm the filters describe, not
   * the page on screen, and including those with NO email address. A firm the
   * Institute cannot email is exactly the row somebody is running this export to
   * find.
   *
   * The server applies the same filters the list was built from, so what
   * downloads is what is being looked at.
   */
  const exportRegistry = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const params = new URLSearchParams();
      const filters: Record<string, string> = {
        search: debouncedSearch,
        status,
        state_id: stateId,
        chapter_id: chapterId,
        approved_from: regFrom,
        approved_to: regTo,
        no_partners: noPartners === "1" ? "1" : "",
        no_account: noAccount === "1" ? "1" : "",
        owing: owing === "1" ? "1" : "",
      };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.set(k, v);
      });

      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/admin/firms/export?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) throw new Error(`Server returned ${res.status}`);

      // This API can emit stray output ahead of a response body, which would
      // land in the file. A JSON body means an error reached us as a download;
      // anything else, skip the leading whitespace and keep the BOM.
      const bytes = new Uint8Array(await res.arrayBuffer());
      const probe = new TextDecoder("latin1").decode(bytes.subarray(0, 4096));

      if (probe.trimStart().startsWith("{")) {
        throw new Error(
          new TextDecoder().decode(bytes.subarray(0, 300)).trim() ||
            "The server did not return a spreadsheet.",
        );
      }

      const start = Math.max(0, probe.search(/\S/));
      const blob = new Blob([bytes.subarray(start)], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `firm-registry-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setExportError(String(e?.message || "Could not export the firm registry."));
    } finally {
      setExporting(false);
    }
  };

  /** Any filter change re-reads from page one — page 4 of a new filter is nothing. */
  const onFilter = (set: (v: any) => void) => (value: any) => {
    set(value);
    setPage(1);
  };

  /**
   * A firm's principal partner. The principal is an ACCOUNT now
   * (firms.principal_user_id), so the name is the account's own rather than name
   * parts to reassemble; the membership number is on the member behind it.
   */
  const principalName = (r: any) =>
    r?.principal?.name || r?.principal?.member?.membership_no || null;

  const principalMembershipNo = (r: any) => r?.principal?.member?.membership_no ?? "";

  /** `state_name` comes from state_id; `state` is the free text older firms carry. */
  const stateOf = (r: any) =>
    r?.state_name ?? (typeof r?.state === "string" ? r.state : null);

  const hasNoPartners = (r: any) => Number(r?.active_partners_count ?? 0) === 0;

  const columns = [
    {
      key: "name",
      header: "Firm",
      accessor: (r: any) => (
        <span className="flex flex-col">
          <span className="font-medium text-gray-900">{r.name ?? "—"}</span>
          <span className="text-[11px] text-gray-400">
            {r.nature_of_practice || r.cama_reg_number || ""}
          </span>
        </span>
      ),
    },
    { key: "firm_no", header: "Firm No" },
    {
      key: "principal",
      header: "Principal",
      sortable: false,
      accessor: (r: any) =>
        principalName(r) ? (
          <span className="flex flex-col">
            <span>{principalName(r)}</span>
            <span className="text-[11px] text-gray-400">
              {principalMembershipNo(r)}
            </span>
          </span>
        ) : (
          <span className="text-gray-300">Not named</span>
        ),
    },
    {
      key: "partners",
      header: "Partners",
      sortable: false,
      // A firm with nobody on its roster is an entry no partner answers for —
      // flagged rather than shown as a bare 0.
      accessor: (r: any) =>
        hasNoPartners(r) ? (
          <span className="text-amber-700 font-bold">None</span>
        ) : (
          <span className="text-gray-700">{r.active_partners_count}</span>
        ),
    },
    {
      key: "chapter",
      header: "Chapter",
      sortable: false,
      accessor: (r: any) => r.chapter?.name ?? "N/A",
    },
    {
      key: "state",
      header: "State",
      sortable: false,
      accessor: (r: any) => stateOf(r) ?? "N/A",
    },
    { key: "email", header: "Email" },
    { key: "phone", header: "Phone" },
    {
      key: "status",
      header: "Status",
      accessor: (r: any) => (
        <span
          className={
            String(r.status) === "active"
              ? "inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200"
              : "inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-300"
          }
        >
          {r.status ?? "—"}
        </span>
      ),
    },
    {
      key: "approved_at",
      header: "Registered",
      accessor: (r: any) => (r.approved_at ? formatDate(r.approved_at) : "—"),
    },
    {
      key: "outstanding_kobo",
      header: "Owes",
      sortable: false,
      // The FIRM's own balance — its invoices, not its principal's. A firm's
      // arrears never appear in a member's balance and never should.
      accessor: (r: any) => {
        const owed = Number(r.outstanding_kobo ?? 0);
        return (
          <span className="flex flex-col items-start gap-1">
            <span className={owed > 0 ? "text-red-600 font-bold" : "text-green-600"}>
              {formatCurrency(owed)}
            </span>
            {/* A firm pays into the bank and sends the teller; this is where it
                gets recorded. Offered only where something is owed. */}
            {owed > 0 && canPay && (
              <button
                onClick={() => openPaymentDialog(r)}
                className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-[10px] font-bold hover:bg-emerald-100 transition-colors"
              >
                Record payment
              </button>
            )}
          </span>
        );
      },
    },
    {
      key: "account",
      header: "Admin account",
      sortable: false,
      // The firm's ADMINISTRATIVE account: the login the practice itself uses
      // to see its own file and settle its own bills, and the address the
      // Institute writes to. NOT who the invoice belongs to — that is the firm
      // itself, which is billed whether an account exists or not.
      accessor: (r: any) =>
        r.account ? (
          <span className="flex flex-col">
            <span className="inline-flex w-fit text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
              Has account
            </span>
            <span className="text-[11px] text-gray-400 mt-0.5">{r.account.email}</span>
          </span>
        ) : canAccount ? (
          <button
            onClick={() => openAccountDialog(r)}
            className="px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-md text-xs font-bold hover:bg-amber-100 transition-colors"
          >
            Add admin account
          </button>
        ) : (
          <span className="text-amber-700 text-xs font-semibold">No account</span>
        ),
    },
    {
      key: "action",
      header: "Action",
      sortable: false,
      // There is no separate firm page — the application IS the firm's file.
      accessor: (r: any) => (
        <span className="flex flex-wrap items-center gap-1.5">
          {r.firm_application_id ? (
            <a
              href={`/admin/firm-applications/${r.firm_application_id}`}
              className="px-3 py-1 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
            >
              View file
            </a>
          ) : (
            <span className="text-gray-300 text-xs">No file</span>
          )}
          {/* Correcting the address and the principal — the two particulars
              the register got from the application and could never revise. */}
          {canEdit && (
            <button
              onClick={() => openEditDialog(r)}
              className="px-3 py-1 bg-gray-100 text-gray-700 border border-gray-200 rounded-md text-xs font-bold hover:bg-gray-200 transition-colors"
            >
              Edit
            </button>
          )}
        </span>
      ),
    },
  ];

  /**
   * One firm as a card, for screens the table does not fit. Ordered by what
   * someone looking a firm up actually wants: its name, its number, who leads
   * it.
   */
  const mobileCard = (r: any) => {
    const place = [r.chapter?.name, stateOf(r)].filter(Boolean).join(" · ");

    return (
      <div className="space-y-2.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-bold text-gray-900 leading-tight break-words">
              {r.name || "—"}
            </div>
            <div className="text-[11px] font-black text-gray-400 uppercase tracking-widest mt-0.5">
              {r.firm_no || "No firm no."}
            </div>
            {r.approved_at && (
              <div className="text-[11px] text-gray-400 font-medium mt-0.5">
                Registered {formatDate(r.approved_at)}
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div
              className={`font-bold text-sm ${
                String(r.status) === "active" ? "text-green-600" : "text-gray-500"
              }`}
            >
              {r.status ?? "—"}
            </div>
            <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
              {hasNoPartners(r) ? "No partners" : `${r.active_partners_count} partner(s)`}
            </div>
            {Number(r.outstanding_kobo ?? 0) > 0 && (
              <div className="text-[11px] font-bold text-red-600 mt-0.5">
                Owes {formatCurrency(r.outstanding_kobo)}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-0.5 text-xs text-gray-500 font-medium break-words">
          {principalName(r) && <div>Principal: {principalName(r)}</div>}
          {r.email && <div>{r.email}</div>}
          {r.phone && <div>{r.phone}</div>}
          {place && <div className="text-[11px] text-gray-400">{place}</div>}
        </div>

        {!r.account && canAccount && (
          <button
            onClick={() => openAccountDialog(r)}
            className="block w-full text-center px-3 py-2.5 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold rounded-lg hover:bg-amber-100 transition-colors"
          >
            Add an administrative account
          </button>
        )}

        {canEdit && (
          <button
            onClick={() => openEditDialog(r)}
            className="block w-full text-center px-3 py-2.5 bg-gray-100 text-gray-700 border border-gray-200 text-xs font-bold rounded-lg hover:bg-gray-200 transition-colors"
          >
            Edit address & principal
          </button>
        )}

        {r.firm_application_id && (
          <a
            href={`/admin/firm-applications/${r.firm_application_id}`}
            className="block text-center px-3 py-2.5 bg-blue-50 text-blue-600 text-xs font-bold rounded-lg hover:bg-blue-100 transition-colors"
          >
            View firm file
          </a>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 bg-white rounded-lg shadow-sm">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Firm Registry</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Every practice firm on the Institute's register — those whose firm number has been
            issued.
          </p>
        </div>

        {/* Counted off the LIST, not the summary: the summary deliberately
            ignores the status filter (its cards are how status is chosen), and
            the export does not — so the list's total is the one that matches
            what will actually download. */}
        {canExport && (
        <button
          onClick={exportRegistry}
          disabled={exporting || !firms?.total}
          title={
            firms?.total
              ? `Download all ${Number(firms.total).toLocaleString()} filtered firm(s), including those with no email address`
              : "No firms match the current filters"
          }
          className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-bold rounded-lg transition-all"
        >
          {exporting
            ? "Preparing…"
            : `Export registry${firms?.total ? ` (${Number(firms.total).toLocaleString()})` : ""}`}
        </button>
        )}
      </div>

      {exportError && (
        <div className="px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm font-semibold text-red-700">
          {exportError}
        </div>
      )}

      {banner && (
        <div className="px-4 py-3 rounded-lg border border-green-200 bg-green-50 text-sm font-semibold text-green-700">
          {banner}
        </div>
      )}

      {/* Money the firm paid at the bank. The gateway is not involved, so every
          field the bank knows has to be typed — and the reference is what ties
          the record back to the statement. */}
      {payFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 space-y-4 my-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Record a bank payment</h3>
              <p className="text-sm text-gray-500 mt-1">
                {payFor.name}
                {payFor.firm_no ? ` · ${payFor.firm_no}` : ""} · owes{" "}
                <span className="font-semibold text-red-600">
                  {formatCurrency(payFor.outstanding_kobo ?? 0)}
                </span>
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600 leading-relaxed">
              Applied to the firm's oldest invoices first. Anything more than the firm owes is
              recorded on the payment but not allocated — a firm has no advance wallet.
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Amount (₦)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-100"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Paid on
                </label>
                <input
                  type="date"
                  value={payPaidAt}
                  onChange={(e) => setPayPaidAt(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-100"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Method
                </label>
                <select
                  value={payMethod}
                  onChange={(e) => setPayMethod(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-emerald-100"
                >
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="cash">Cash</option>
                  <option value="pos">POS</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Paid into
                </label>
                <select
                  value={payBankAccountId}
                  onChange={(e) => setPayBankAccountId(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-emerald-100"
                >
                  <option value="">Choose account…</option>
                  {(bankAccounts as any[]).map((a: any) => (
                    <option key={a.id} value={a.id}>{ledgerAccountLabel(a)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Teller / transfer reference
              </label>
              <input
                value={payReference}
                onChange={(e) => setPayReference(e.target.value)}
                placeholder="As it appears on the bank statement"
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-100"
              />
              <p className="text-[11px] text-gray-400">
                Recorded once only — the same reference cannot be entered twice.
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Proof of payment (optional)
              </label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => setPayProof(e.target.files?.[0] ?? null)}
                className="w-full text-xs"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Notes (optional)
              </label>
              <textarea
                value={payNotes}
                onChange={(e) => setPayNotes(e.target.value)}
                rows={2}
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-100 resize-none"
              />
            </div>

            {payError && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {payError}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={() => setPayFor(null)}
                disabled={recording}
                className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitPayment}
                disabled={
                  recording || !payAmount || !payReference.trim() || !payPaidAt || !payBankAccountId
                }
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40"
              >
                {recording ? "Recording…" : "Record payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attaching an account to a firm that has none. Deliberately a short
          form: the only decision is which address the firm's account carries —
          everything else follows from the firm itself, and the invoice is
          raised by the same request. */}
      {accountFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 space-y-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Add an administrative account</h3>
              <p className="text-sm text-gray-500 mt-1">
                {accountFor.name}
                {accountFor.firm_no ? ` · ${accountFor.firm_no}` : ""}
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600 leading-relaxed">
              This creates the <span className="font-semibold">firm's own administrative
              account</span> — the login the practice uses to see its own file and settle its own
              bills, and the address the Institute writes to. It carries no privileges over anybody
              else's record: it reaches that firm and nothing more. The dues invoice belongs to the
              firm itself, not to this account, and is raised here if it has not been already. Do
              not use a member's personal address: the firm's correspondence would go to the wrong
              person.
              <span className="block mt-2 font-semibold text-gray-700">
                A temporary password is emailed to this address straight away, and the firm must
                change it at its first sign-in — so check the address before pressing.
              </span>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Firm email
              </label>
              <input
                type="email"
                value={accountEmail}
                onChange={(e) => setAccountEmail(e.target.value)}
                placeholder="accounts@firm.com"
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-100"
              />
              {!accountFor.email && (
                <p className="text-[11px] text-amber-600">
                  The register holds no email for this firm — you will need one before it can have
                  an account.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Account name
              </label>
              <input
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder={accountFor.name}
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-100"
              />
            </div>

            {accountError && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {accountError}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={() => setAccountFor(null)}
                disabled={creatingAccount}
                className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitAccount}
                disabled={creatingAccount || !accountEmail.trim()}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40"
              >
                {creatingAccount ? "Creating…" : "Create account & raise invoice"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Correcting the register. Two fields, and deliberately only two: the
          firm's NAME, number, chapter and status are what the register
          asserts about a registration, and revising those is registering a
          different firm — which belongs to the applications queue. */}
      {editFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Edit firm particulars</h3>
              <p className="text-sm text-gray-500 mt-1">
                {editFor.name}
                {editFor.firm_no ? ` · ${editFor.firm_no}` : ""}
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Correspondence address
              </label>
              <input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="info@firm.com"
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-100"
              />
              <p className="text-[11px] text-gray-400">
                The firm's own address, as the register holds it. Separate from the administrative
                account's sign-in address — changing this does not change who can sign in, and
                leaving it empty is allowed.
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Principal partner
              </label>

              {/* What is set now, and the way to let it go. Shown rather than
                  implied: a picker that starts empty reads as "there is no
                  principal", which for most of the register is untrue. */}
              <div className="flex items-center justify-between gap-2 rounded-lg border bg-gray-50 px-3 py-2">
                <span className="text-sm text-gray-800">
                  {editPrincipalId ? editPrincipalLabel || `User #${editPrincipalId}` : "None"}
                </span>
                {editPrincipalId !== null && (
                  <button
                    onClick={() => {
                      setEditPrincipalId(null);
                      setEditPrincipalLabel("");
                    }}
                    className="text-[11px] font-bold text-red-600 hover:underline shrink-0"
                  >
                    Clear
                  </button>
                )}
              </div>

              <input
                value={principalSearch}
                onChange={(e) => setPrincipalSearch(e.target.value)}
                placeholder="Search a member by name or membership number…"
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-100 mt-1"
              />

              {principalSearch.trim().length > 0 && principalSearch.trim().length < 3 && (
                <p className="text-[11px] text-gray-400">Keep typing — at least three characters.</p>
              )}

              {searchingPrincipals && (
                <p className="text-[11px] text-gray-400">Searching…</p>
              )}

              {principalTerm.trim().length >= 3 && !searchingPrincipals && (
                <div className="border rounded-lg divide-y max-h-52 overflow-y-auto">
                  {principalResults.length === 0 && (
                    <p className="px-3 py-2 text-[11px] text-gray-400">
                      No member matches. Only Members and Fellows can be a principal partner.
                    </p>
                  )}
                  {principalResults.map((m: any) => {
                    const name = [m.surname, m.firstname, m.othernames]
                      .filter(Boolean)
                      .join(" ")
                      .trim();
                    const label = `${name}${m.membership_no ? ` · ${m.membership_no}` : ""}`;

                    return (
                      <button
                        key={m.id}
                        onClick={() => {
                          setEditPrincipalId(m.id);
                          setEditPrincipalLabel(label);
                          setPrincipalSearch("");
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50"
                      >
                        <div className="text-sm text-gray-800">{name || `User #${m.id}`}</div>
                        <div className="text-[11px] text-gray-400">
                          {m.membership_no || "no number"}
                          {m.grade ? ` · ${m.grade}` : ""}
                          {/* The search flags a member who is already on an
                              active firm's roster — `blocked_by` is that
                              firm's NAME. Worth seeing before picking, though
                              it is not itself a refusal: being somebody's
                              partner does not stop a member being named the
                              principal here. Only already being another firm's
                              PRINCIPAL does, and the server says so. */}
                          {m.eligible === false && m.blocked_by
                            ? ` · partner in ${m.blocked_by}`
                            : ""}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <p className="text-[11px] text-gray-400">
                The member the firm is registered under. They can see the firm and pay its dues. A
                member may be the principal of one firm only.
              </p>
            </div>

            {editError && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {editError}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={() => setEditFor(null)}
                disabled={savingEdit}
                className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitEdit}
                disabled={savingEdit}
                className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-black disabled:opacity-40"
              >
                {savingEdit ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The whole filtered register, not the page on screen. Each card is also
          the control for the filter it counts. */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {[
          { label: "Registered firms", value: summary?.total, tone: "bg-gray-100 text-gray-800 border-gray-200", on: status === "" && noPartners === "", set: () => { onFilter(setStatus)(""); setNoPartners(""); } },
          { label: "Active", value: summary?.active, tone: "bg-green-100 text-green-800 border-green-200", on: status === "active", set: () => onFilter(setStatus)("active") },
          { label: "Inactive", value: summary?.inactive, tone: "bg-gray-200 text-gray-700 border-gray-300", on: status === "inactive", set: () => onFilter(setStatus)("inactive") },
          { label: "No partners on roster", value: summary?.no_partners, tone: "bg-amber-100 text-amber-800 border-amber-200", on: noPartners === "1", set: () => onFilter(setNoPartners)(noPartners === "1" ? "" : "1") },
          // Billed, but with nobody to send it to — the work list.
          { label: "No account to bill to", value: summary?.no_account, tone: "bg-rose-100 text-rose-800 border-rose-200", on: noAccount === "1", set: () => onFilter(setNoAccount)(noAccount === "1" ? "" : "1") },
          { label: `Owing · ${formatCurrency(summary?.owing_kobo ?? 0)}`, value: summary?.owing, tone: "bg-orange-100 text-orange-800 border-orange-200", on: owing === "1", set: () => onFilter(setOwing)(owing === "1" ? "" : "1") },
        ].map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={c.set}
            className={`border rounded-xl p-3 text-left transition hover:opacity-80 ${c.tone} ${
              c.on ? "ring-2 ring-offset-1 ring-gray-400" : ""
            }`}
          >
            <div className="text-2xl font-bold">{c.value ?? "—"}</div>
            <div className="text-xs font-medium opacity-80">{c.label}</div>
          </button>
        ))}
      </div>

      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4">
        <h3 className="font-semibold text-gray-700 hidden lg:block">Filter Firms</h3>

        {/* Search stays out in the open — looking a firm up is what this page is
            for. The narrowing filters fold away on a phone. */}
        <div className="flex gap-2 lg:hidden">
          <input
            placeholder="Firm, number, principal…"
            value={search}
            onChange={(e) => onFilter(setSearch)(e.target.value)}
            className="flex-1 min-w-0 border px-3 py-2.5 rounded-lg text-sm focus:border-blue-500 outline-none bg-white"
          />
          <button
            onClick={() => setShowFilters((p) => !p)}
            className="shrink-0 flex items-center gap-1.5 border px-3 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest text-gray-600 bg-white"
          >
            Filters
            {activeFilterCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-white text-[9px]">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        <div className={`${showFilters ? "grid" : "hidden"} lg:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3`}>
          <input
            placeholder="Firm, number, principal, CAC no…"
            value={search}
            onChange={(e) => onFilter(setSearch)(e.target.value)}
            className="hidden lg:block border px-3 py-2 rounded-lg text-sm focus:border-blue-500 outline-none"
          />

          <select
            value={status}
            onChange={(e) => onFilter(setStatus)(e.target.value)}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={stateId}
            onChange={(e) => onFilter(setStateId)(e.target.value)}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All States</option>
            {states?.map((s: any) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          <select
            value={chapterId}
            onChange={(e) => onFilter(setChapterId)(e.target.value)}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All Chapters</option>
            {chapters?.map((c: any) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className={`${showFilters ? "grid" : "hidden"} lg:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3`}>
          {/* Date the number was issued. Labelled, because two bare date boxes do
              not say what date they are about — and either end works alone. */}
          <label className="flex items-center gap-2 border px-3 py-2 rounded-lg text-sm bg-white">
            <span className="shrink-0 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Reg. from
            </span>
            <input
              type="date"
              value={regFrom}
              onChange={(e) => onFilter(setRegFrom)(e.target.value)}
              className="w-full min-w-0 outline-none text-sm"
            />
          </label>

          <label className="flex items-center gap-2 border px-3 py-2 rounded-lg text-sm bg-white">
            <span className="shrink-0 text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Reg. to
            </span>
            <input
              type="date"
              value={regTo}
              onChange={(e) => onFilter(setRegTo)(e.target.value)}
              className="w-full min-w-0 outline-none text-sm"
            />
          </label>

          <select
            value={noPartners}
            onChange={(e) => onFilter(setNoPartners)(e.target.value)}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All rosters</option>
            <option value="1">No partners on roster</option>
          </select>

          <select
            value={noAccount}
            onChange={(e) => onFilter(setNoAccount)(e.target.value)}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All accounts</option>
            <option value="1">No account to bill to</option>
          </select>

          <select
            value={owing}
            onChange={(e) => onFilter(setOwing)(e.target.value)}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value="">All balances</option>
            <option value="1">Owing dues</option>
          </select>

          <select
            value={perPage}
            onChange={(e) => onFilter(setPerPage)(Number(e.target.value))}
            className="border px-3 py-2.5 lg:py-2 rounded-lg text-sm bg-white"
          >
            <option value={25}>25 Rows</option>
            <option value={50}>50 Rows</option>
            <option value={100}>100 Rows</option>
          </select>

          <button
            onClick={() => {
              setSearch("");
              setStatus("");
              setStateId("");
              setChapterId("");
              setRegFrom("");
              setRegTo("");
              setNoPartners("");
              setNoAccount("");
              setOwing("");
              setPage(1);
            }}
            className="text-red-600 text-sm font-medium hover:bg-red-50 py-2.5 lg:py-2 px-4 rounded-lg transition-colors border lg:border-0 border-red-100 sm:col-span-2 lg:col-span-1"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* An error is NOT an empty register. Rendering a blank table for a request
          that never arrived says the Institute has no firms, which is a claim
          about the data rather than about the request that failed. */}
      {isError ? (
        <div className="px-4 py-4 rounded-xl border border-red-200 bg-red-50 space-y-1">
          <p className="text-sm font-semibold text-red-700">
            Could not load the firm register.
          </p>
          <p className="text-sm text-red-600">
            {(listError as any)?.data?.message ||
              "The server did not answer. This is a problem with the request, not an empty register."}
          </p>
          <p className="text-[11px] font-mono text-red-400">
            {String((listError as any)?.status ?? "")} · GET /admin/firms
          </p>
        </div>
      ) : isLoading ? (
        <TableSkeleton />
      ) : (
        <DataTable
          title="Firm Registry"
          columns={columns}
          data={firms?.data || []}
          pagination={firms}
          onPageChange={setPage}
          sort={sort}
          setSort={setSort}
          dir={dir}
          setDir={setDir}
          selected={selected}
          setSelected={setSelected}
          mobileCard={mobileCard}
          rowClass={(r: any) =>
            String(r.status) !== "active"
              ? "bg-gray-200/70 text-gray-500 hover:bg-gray-300/70"
              : ""
          }
        />
      )}
    </div>
  );
}
